import { IconButton } from "@material-ui/core";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import { DateTime } from "luxon";
import { forwardRef } from "react";

import { DraggableTime, DraggableTimeProps } from "@/common/components/Draggable/DraggableTime/DraggableTime";
import { useDispatch } from "@/store";
import { setTimelineStart } from "@/store/archives/slice";

import { Tick } from "./Tick";
import { useStyles } from "./Timeline.styles";


/**
 * @typedef TimelineProps
 * @extends DraggableTimeProps
 * @prop {number} intervalDuration - Duration point where a minor tick will appear
 * @prop {number} [majorIntervalEvery] - Number of intervals until a major tick
 */
interface TimelineProps extends Omit<DraggableTimeProps, "children"> {
    intervalDuration: number;
    majorIntervalEvery?: number;
    selectedTime: DateTime | null;
}


/**
 * A draggable timeline component.
 */
export const Timeline = forwardRef<HTMLDivElement, TimelineProps>((props, ref) => {
    const { duration, time, intervalDuration, majorIntervalEvery = 10, selectedTime, ...rest } = props;
    const classes = useStyles();
    const dispatch = useDispatch();

    const numTicks = duration / intervalDuration;
    const ticks = [];

    const timeMs = time.toMillis();
    const flooredTime = DateTime.fromMillis(Math.floor(timeMs / intervalDuration) * intervalDuration, { zone: time.zone });

    for (let i = -1; i < numTicks + 1; ++i) {
        const tickTime = flooredTime.plus({ milliseconds: i * intervalDuration });
        const tickTimeMs = tickTime.toMillis();
        const offsetPercent = ((tickTimeMs - timeMs) * 100) / duration;
        const major = tickTimeMs % (majorIntervalEvery * intervalDuration) == 0;
        const label = major ? tickTime.toLocaleString(DateTime.TIME_SIMPLE) : undefined;
        const key = tickTime.toISO();

        ticks.push(<Tick label={label} key={key} offset={offsetPercent} major={major} />);
    }

    const handleArrowClick = (newTime: DateTime) => dispatch(setTimelineStart(newTime.toISO()));

    return (
        <DraggableTime {...rest} time={time} duration={duration}>
            <div
                className={classes.timeline}
                ref={ref}
            >
                <IconButton aria-label="chevron-left" onClick={() => selectedTime && handleArrowClick(selectedTime.minus({ hours: 1 }))}>
                    <ChevronLeft className={classes.arrow} />
                </IconButton>
                <div className={classes.tickContainer}>
                    {ticks}
                </div>
                <div className={classes.triangle} />
                <IconButton aria-label="chevron-right" onClick={() => selectedTime && handleArrowClick(selectedTime)}>
                    <ChevronRight className={classes.arrow} />
                </IconButton>
            </div>
        </DraggableTime>
    );
});

Timeline.displayName = "Timeline";
